
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import Dashboard from './pages/Dashboard';
import StockList from './pages/StockList';
import ReplenishmentPlanning from './pages/ReplenishmentPlanning';
import EnterpriseRequestCart from './pages/EnterpriseRequestCart';
import WarningLevels from './pages/WarningLevels';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Onboarding from './pages/Onboarding';
import GeneralSettings from './pages/settings/GeneralSettings';
import UserManagement from './pages/settings/UserManagement';
import UserDetail from './pages/settings/UserDetail';
import ChannelSettings from './pages/settings/ChannelSettings';
import PlanSettings from './pages/settings/PlanSettings';
import SupplierDashboard from './pages/SupplierDashboard';
import SupplierProductList from './pages/SupplierProductList';
import SupplierOrders from './pages/SupplierOrders';
import SupplierOrderRequests from './pages/SupplierOrderRequests';
import SupplierCart from './pages/SupplierCart';
import SupplierCustomers from './pages/SupplierCustomers';
import SupplierCustomerDetail from './pages/SupplierCustomerDetail';
import EnterpriseOrders from './pages/EnterpriseOrders';
import OrderTracking from './pages/OrderTracking';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';

const AppContent: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsTransitioning(true);
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 200);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const queryParams = new URLSearchParams(location.search);
  const hideShell = queryParams.get('shell') === 'false';
  
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup' || location.pathname === '/onboarding';

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Handle Supplier Redirects
  if (user?.role === 'supplier' && location.pathname === '/') {
     return <Navigate to="/supplier-dashboard" replace />;
  }

  // Prevent Supplier from accessing Enterprise pages
  if (user?.role === 'supplier' && !location.pathname.startsWith('/supplier')) {
     return <Navigate to="/supplier-dashboard" replace />;
  }

  if (isAuthPage && (hideShell || location.pathname === '/onboarding')) {
    return (
      <Routes>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/signup" element={<Navigate to="/" replace />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <div className="flex h-screen bg-[#F5F7FA] dark:bg-[#050505] overflow-hidden font-sans text-slate-900 dark:text-gray-100 transition-colors duration-300 w-full max-w-full">
      {!hideShell && <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />}

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative w-full">
        {!hideShell && <Topbar toggleSidebar={() => setCollapsed(!collapsed)} collapsed={collapsed} />}
        
        <main className={`flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar bg-[#F5F7FA] dark:bg-[#050505] transition-all duration-300 ease-in-out ${isTransitioning ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'} w-full`}>
          <div className={`min-w-0 h-full w-full max-w-full ${hideShell ? 'p-0' : ''}`}>
            <Routes>
              {/* Enterprise Routes */}
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Navigate to="/" replace />} />
              <Route path="/stock" element={<StockList />} />
              <Route path="/replenishment" element={<ReplenishmentPlanning />} />
              <Route path="/request-cart" element={<EnterpriseRequestCart />} />
              <Route path="/orders" element={<EnterpriseOrders />} />
              <Route path="/orders/track/:orderId" element={<OrderTracking />} />
              <Route path="/warnings" element={<WarningLevels />} />
              
              <Route path="/settings" element={<Navigate to="/settings/general" replace />} />
              <Route path="/settings/general" element={<GeneralSettings />} />
              <Route path="/settings/users" element={<UserManagement />} />
              <Route path="/settings/users/:userId" element={<UserDetail />} />
              <Route path="/settings/channels" element={<ChannelSettings />} />
              <Route path="/settings/plan" element={<PlanSettings />} />
              
              {/* Supplier Routes */}
              <Route path="/supplier-dashboard" element={<SupplierDashboard />} />
              <Route path="/supplier-products" element={<SupplierProductList />} />
              <Route path="/supplier-customers" element={<SupplierCustomers />} />
              <Route path="/supplier-customers/:customerId" element={<SupplierCustomerDetail />} />
              <Route path="/supplier-order-requests" element={<SupplierOrderRequests />} />
              <Route path="/supplier-orders" element={<SupplierOrders />} />
              <Route path="/supplier-cart" element={<SupplierCart />} />

              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <AuthProvider>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </AuthProvider>
    </LanguageProvider>
  );
};

export default App;
